<script setup lang="ts">
import { computed } from 'vue'
import { formatAed, formatToInternational } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { TokenDiamondIcon } from '@uiKit/icons'
import SellAmountField from './SellAmountField.vue'
import SellSummaryCard, { type SellSummaryRow } from './SellSummaryCard.vue'
import SellNote from './SellNote.vue'
import { LOCKED_TOKENS_NOTE } from '../constants/sellCopy'
import { formatTokenCount, parseTokens, sanitizeTokens } from '../utils/sellListingUtils'

// Sell 3 — Quantity (Figma 34796:138630): how many tokens to list, then what
// the holding looks like once they're listed.

interface Props {
  tokensHeld: number
  available: number
  /** Minimum listing value in AED (sell-overview). */
  minListingAmount: number
  error: string | null
}

const props = defineProps<Props>()

const tokensText = defineModel<string>('tokens', { required: true })

const emit = defineEmits<{ (e: 'blur'): void }>()

const tokens = computed(() => parseTokens(tokensText.value))

const hint = computed(
  () =>
    `${formatToInternational(props.available)} available only · minimum listing value ${formatAed(props.minListingAmount)}`,
)

const rows = computed<SellSummaryRow[]>(() => [
  { label: 'Tokens held', value: formatTokenCount(props.tokensHeld) },
  { label: 'Tokens Listing', value: formatTokenCount(tokens.value) },
  {
    label: 'Token Balance after this',
    value:
      tokens.value > 0 ? formatToInternational(Math.max(0, props.tokensHeld - tokens.value)) : '-',
    isTotal: true,
  },
])
</script>

<template>
  <Box direction="col" gap="4">
    <Text
      is="h3"
      type="caption"
      weight="bold"
      color="var(--p-sliceal-gray-400)"
      class="sell-quantity__eyebrow"
      >HOW MANY TOKENS TO LIST?</Text
    >

    <SellAmountField
      v-model="tokensText"
      :sanitize="sanitizeTokens"
      label="Tokens to list"
      inputmode="numeric"
      described-by-id="sell-tokens-hint"
      :hint="hint"
      :error="error"
      @blur="emit('blur')"
    >
      <template #prefix>
        <TokenDiamondIcon :size="36" />
      </template>
    </SellAmountField>

    <SellSummaryCard :rows="rows" />

    <SellNote tone="warning" :title="LOCKED_TOKENS_NOTE.title" :text="LOCKED_TOKENS_NOTE.text" />
  </Box>
</template>

<style scoped>
.sell-quantity__eyebrow {
  margin: 0;
  line-height: 1.4;
}
</style>
